import { useState, useEffect } from 'react'
import { Plus, Search, MoreHorizontal, Edit, Trash2, Eye, Users, Building } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import { supabase } from '@/integrations/supabase/client'
import { auditLogger } from '@/utils/auditLogger'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { validateEmail, validateName } from '@/utils/inputValidation'
import { useAuth } from '@/hooks/useAuth'

interface Tenant {
  id: string
  name: string
  slug: string
  admin_user_id: string
  domain?: string
  status: 'active' | 'suspended' | 'pending' | 'trial'
  created_at: string
  admin_email?: string
}

interface UnassignedCustomer {
  id: string
  email: string
  full_name: string
  first_name?: string
  last_name?: string
  phone?: string
  created_at: string
  visit_stamps: number
  referral_stamps: number
  total_visits: number
}

const TenantManagement = () => {
  const { user, userRole } = useAuth()
  const [tenants, setTenants] = useState<Tenant[]>([])
  const [filteredTenants, setFilteredTenants] = useState<Tenant[]>([])
  const [unassignedCustomers, setUnassignedCustomers] = useState<UnassignedCustomer[]>([])
  const [filteredCustomers, setFilteredCustomers] = useState<UnassignedCustomer[]>([])
  const [loading, setLoading] = useState(true)
  const [customerLoading, setCustomerLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [customerSearchTerm, setCustomerSearchTerm] = useState('')
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false)
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false)
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null)
  const [selectedCustomer, setSelectedCustomer] = useState<UnassignedCustomer | null>(null)
  const [selectedTenantForAssignment, setSelectedTenantForAssignment] = useState<string>('')
  const [newTenant, setNewTenant] = useState<{
    name: string
    slug: string
    adminEmail: string
    domain: string
    status: 'active' | 'suspended' | 'pending' | 'trial'
  }>({
    name: '',
    slug: '',
    adminEmail: '',
    domain: '',
    status: 'pending'
  })
  const [editTenant, setEditTenant] = useState<{
    name: string
    slug: string
    domain: string
    status: 'active' | 'suspended' | 'pending' | 'trial'
  }>({
    name: '',
    slug: '',
    domain: '',
    status: 'pending'
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const { toast } = useToast()

  useEffect(() => {
    fetchTenants()
    fetchUnassignedCustomers()
  }, [])

  useEffect(() => {
    // Filter tenants based on search term
    if (searchTerm.trim() === '') {
      setFilteredTenants(tenants)
    } else {
      const filtered = tenants.filter(tenant =>
        tenant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tenant.slug.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tenant.admin_email?.toLowerCase().includes(searchTerm.toLowerCase())
      )
      setFilteredTenants(filtered)
    }
  }, [tenants, searchTerm])

  useEffect(() => {
    // Filter customers based on search term
    if (customerSearchTerm.trim() === '') {
      setFilteredCustomers(unassignedCustomers)
    } else {
      const filtered = unassignedCustomers.filter(customer =>
        customer.full_name.toLowerCase().includes(customerSearchTerm.toLowerCase()) ||
        customer.email.toLowerCase().includes(customerSearchTerm.toLowerCase()) ||
        customer.first_name?.toLowerCase().includes(customerSearchTerm.toLowerCase()) ||
        customer.last_name?.toLowerCase().includes(customerSearchTerm.toLowerCase())
      )
      setFilteredCustomers(filtered)
    }
  }, [unassignedCustomers, customerSearchTerm])

  const fetchTenants = async () => {
    try {
      // Get tenants first
      const { data: tenantsData, error: tenantsError } = await supabase
        .from('tenants')
        .select('*')
        .order('created_at', { ascending: false })

      if (tenantsError) throw tenantsError

      // Get admin emails from auth.users via a more reliable method
      const tenantsWithEmails = await Promise.all(
        (tenantsData || []).map(async (tenant) => {
          if (tenant.admin_user_id) {
            try {
              // Try profiles table first, then fallback to a direct query
              const { data: profileData } = await supabase
                .from('profiles')
                .select('email')
                .eq('user_id', tenant.admin_user_id)
                .maybeSingle()

              if (profileData?.email) {
                return {
                  ...tenant,
                  admin_email: profileData.email
                }
              }

              // Fallback: Query user_roles to get the user info
              const { data: roleData } = await supabase
                .from('user_roles') 
                .select('user_id')
                .eq('user_id', tenant.admin_user_id)
                .eq('role', 'admin')
                .maybeSingle()

              if (roleData) {
                // If we know it's an admin, try to get email from tenant creation data
                // For now, show a placeholder until profile is created
                return {
                  ...tenant,
                  admin_email: 'Admin (Profil wird erstellt...)'
                }
              }
            } catch (error) {
              console.error('Error getting admin email:', error)
            }
          }
          return {
            ...tenant,
            admin_email: 'N/A'
          }
        })
      )

      setTenants(tenantsWithEmails)
      await auditLogger.logDataAccess('tenants', undefined, 'view')
    } catch (error: any) {
      console.error('Error fetching tenants:', error)
      toast({
        variant: "destructive",
        title: "Fehler beim Laden",
        description: "Unternehmen konnten nicht geladen werden."
      })
    } finally {
      setLoading(false)
    }
  }

  const fetchUnassignedCustomers = async () => {
    setCustomerLoading(true)
    try {
      console.log('🔍 Fetching unassigned customers...')
      
      // Erst aktuelle Tenants laden, um sicherzustellen dass die Liste aktuell ist
      const { data: currentTenants, error: tenantsError } = await supabase
        .from('tenants')
        .select('id, name')
        .eq('status', 'active')

      if (tenantsError) {
        console.error('Error fetching current tenants:', tenantsError)
        throw tenantsError
      }

      // Hole alle Kunden und schließe Admin-Benutzer aus
      const { data: customersData, error: customersError } = await supabase
        .from('customers')
        .select(`
          id,
          email,
          full_name,
          first_name,
          last_name,
          phone,
          created_at,
          visit_stamps,
          referral_stamps,
          total_visits,
          tenant_id,
          user_id
        `)
        .order('created_at', { ascending: false })

      if (customersError) throw customersError

      console.log('📊 Raw customers data:', customersData?.length || 0, 'customers')

      // Hole Admin und Superadmin-Benutzer IDs um sie auszuschließen
      const { data: adminUserIds } = await supabase
        .from('user_roles')
        .select('user_id')
        .in('role', ['admin', 'superadmin'])

      const adminIds = new Set(adminUserIds?.map(r => r.user_id) || [])
      
      // Filtere nur Kunden die wirklich keinen tenant_id haben (NULL) und keine Admins/Superadmins sind
      const unassigned = (customersData || []).filter(customer => {
        const isUnassigned = customer.tenant_id === null || customer.tenant_id === undefined
        const isNotAdmin = !adminIds.has(customer.user_id)
        const shouldInclude = isUnassigned && isNotAdmin
        console.log(`Customer ${customer.email}: tenant_id=${customer.tenant_id}, user_id=${customer.user_id}, isAdmin=${!isNotAdmin}, shouldInclude=${shouldInclude}`)
        return shouldInclude
      })

      console.log('✅ Found', unassigned.length, 'unassigned customers')
      setUnassignedCustomers(unassigned)
      await auditLogger.logDataAccess('customers', undefined, 'view')
    } catch (error: any) {
      console.error('Error fetching unassigned customers:', error)
      toast({
        variant: "destructive",
        title: "Fehler beim Laden",
        description: "Unzugeordnete Kunden konnten nicht geladen werden."
      })
    } finally {
      setCustomerLoading(false)
    }
  }

  const validateTenantForm = () => {
    const newErrors: Record<string, string> = {}

    if (!validateName(newTenant.name)) {
      newErrors.name = 'Unternehmensname ist erforderlich (max. 50 Zeichen)'
    }

    if (!newTenant.slug || !/^[a-z0-9-]+$/.test(newTenant.slug)) {
      newErrors.slug = 'Slug darf nur Kleinbuchstaben, Zahlen und Bindestriche enthalten'
    }

    if (!validateEmail(newTenant.adminEmail)) {
      newErrors.adminEmail = 'Bitte geben Sie eine gültige E-Mail-Adresse ein'
    }

    if (newTenant.domain) {
      // Remove protocol if present for validation
      const domain = newTenant.domain.replace(/^https?:\/\//, '')
      if (!/^[a-zA-Z0-9][a-zA-Z0-9.-]{1,61}[a-zA-Z0-9]\.[a-zA-Z]{2,}$/.test(domain)) {
        newErrors.domain = 'Bitte geben Sie eine gültige Domain oder URL ein'
      }
    }

    return newErrors
  }

  const createTenant = async () => {
    console.log('🚀 CREATE TENANT FUNCTION CALLED - THIS SHOULD APPEAR!')
    console.log('Current state:', { userRole, user: user?.email, loading })
    
    const validationErrors = validateTenantForm()
    if (Object.keys(validationErrors).length > 0) {
      console.log('❌ Validation errors:', validationErrors)
      setErrors(validationErrors)
      return
    }

    console.log('✅ Validation passed, proceeding with creation...')
    setErrors({})
    setLoading(true)

    try {
      console.log('=== Starting tenant creation process ===')
      
      const { data: { session } } = await supabase.auth.getSession()
      console.log('Session check:', { hasSession: !!session, hasToken: !!session?.access_token })
      
      if (!session?.access_token) {
        throw new Error('No active session')
      }

      console.log('Preparing to call edge function with data:', {
        name: newTenant.name,
        slug: newTenant.slug,
        adminEmail: newTenant.adminEmail,
        domain: newTenant.domain,
        status: newTenant.status
      })

      // Call edge function to create tenant and admin user
      console.log('Invoking edge function: create-tenant-admin')
      const { data, error } = await supabase.functions.invoke('create-tenant-admin', {
        body: {
          name: newTenant.name,
          slug: newTenant.slug,
          adminEmail: newTenant.adminEmail,
          domain: newTenant.domain,
          status: newTenant.status,
        },
        // Let supabase-js attach the Authorization header and JSON content-type automatically
        // method defaults to POST
      })

      console.log('Edge function completed. Response:', { 
        data, 
        error, 
        hasData: !!data,
        hasError: !!error,
        errorType: typeof error,
        errorMessage: error?.message 
      })
      
      if (error) {
        console.error('Edge function error details:', {
          error,
          message: error.message,
          stack: error.stack,
          name: error.name
        })
        
        // Try to get detailed error from response
        let detailedError = error.message || 'Unknown error'
        try {
          if (error.context && typeof error.context.json === 'function') {
            const errorResponse = await error.context.json()
            if (errorResponse.error) {
              detailedError = errorResponse.error
              if (errorResponse.errorCode) {
                detailedError += ` (Code: ${errorResponse.errorCode})`
              }
              if (errorResponse.details) {
                detailedError += ` - ${errorResponse.details}`
              }
            }
          }
        } catch (parseError) {
          console.log('Could not parse error context:', parseError)
        }
        
        throw new Error(`Function error: ${detailedError}`)
      }

      if (!data?.success) {
        console.error('Function returned unsuccessful:', data)
        throw new Error(data?.error || 'Failed to create tenant')
      }

      console.log('Tenant creation successful, logging audit...')

      await auditLogger.logAdminAction('create_tenant', 'tenants', undefined, {
        tenant_name: newTenant.name,
        admin_email: newTenant.adminEmail
      })

      toast({
        title: "Unternehmen erstellt",
        description: `${newTenant.name} wurde erfolgreich erstellt.`
      })

      // Reset form and refresh data
      setNewTenant({
        name: '',
        slug: '',
        adminEmail: '',
        domain: '',
        status: 'pending'
      })
      setIsCreateModalOpen(false)
      fetchTenants()
      fetchUnassignedCustomers() // Also refresh unassigned customers

    } catch (error: any) {
      console.error('=== Tenant creation failed ===', {
        error,
        message: error?.message,
        stack: error?.stack,
        name: error?.name,
        cause: error?.cause
      })
      
      toast({
        title: "Fehler beim Erstellen des Tenants",
        description: error?.message || "Ein unbekannter Fehler ist aufgetreten",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
      console.log('=== Tenant creation process completed ===')
    }
  }

  const getStatusBadge = (status: string) => {
    const variants = {
      active: 'bg-green-100 text-green-800 hover:bg-green-100',
      suspended: 'bg-red-100 text-red-800 hover:bg-red-100', 
      pending: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100'
    }
    
    const labels = {
      active: 'Aktiv',
      suspended: 'Gesperrt',
      pending: 'Ausstehend'
    }

    return (
      <Badge className={variants[status as keyof typeof variants]}>
        {labels[status as keyof typeof labels]}
      </Badge>
    )
  }

  // Auto-generate slug from name
  const handleNameChange = (name: string) => {
    setNewTenant(prev => ({
      ...prev,
      name,
      slug: name.toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '')
    }))
  }

  const handleEditTenant = (tenant: Tenant) => {
    setSelectedTenant(tenant)
    setEditTenant({
      name: tenant.name,
      slug: tenant.slug,
      domain: tenant.domain || '',
      status: tenant.status
    })
    setIsEditModalOpen(true)
  }

  const handleViewDetails = (tenant: Tenant) => {
    setSelectedTenant(tenant)
    setIsDetailsModalOpen(true)
  }

  const updateTenant = async () => {
    if (!selectedTenant) return

    setLoading(true)
    try {
      const { error } = await supabase
        .from('tenants')
        .update({
          name: editTenant.name,
          slug: editTenant.slug,
          domain: editTenant.domain || null,
          status: editTenant.status
        })
        .eq('id', selectedTenant.id)

      if (error) throw error

      toast({
        title: "Unternehmen aktualisiert",
        description: `${editTenant.name} wurde erfolgreich aktualisiert.`
      })

      setIsEditModalOpen(false)
      fetchTenants()
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Fehler beim Aktualisieren",
        description: error.message
      })
    } finally {
      setLoading(false)
    }
  }

  const handleAssignCustomer = (customer: UnassignedCustomer) => {
    setSelectedCustomer(customer)
    setSelectedTenantForAssignment('')
    setIsAssignModalOpen(true)
  }

  const assignCustomerToTenant = async () => {
    if (!selectedCustomer || !selectedTenantForAssignment) return

    setLoading(true)
    try {
      console.log('🔄 Assigning customer', selectedCustomer.email, 'to tenant', selectedTenantForAssignment)
      
      const { error } = await supabase
        .from('customers')
        .update({ tenant_id: selectedTenantForAssignment })
        .eq('id', selectedCustomer.id)

      if (error) throw error

      console.log('✅ Customer assignment successful')

      toast({
        title: "Kunde zugewiesen",
        description: `${selectedCustomer.full_name} wurde erfolgreich einem Unternehmen zugewiesen.`
      })

      await auditLogger.logAdminAction('assign_customer_to_tenant', 'customers', selectedCustomer.id, {
        customer_name: selectedCustomer.full_name,
        customer_email: selectedCustomer.email,
        tenant_id: selectedTenantForAssignment,
        tenant_name: tenants.find(t => t.id === selectedTenantForAssignment)?.name
      })

      setIsAssignModalOpen(false)
      
      // Warte kurz und lade dann die Liste neu
      setTimeout(async () => {
        console.log('🔄 Refreshing unassigned customers list...')
        await fetchUnassignedCustomers()
      }, 500)
      
    } catch (error: any) {
      console.error('❌ Error assigning customer:', error)
      toast({
        variant: "destructive",
        title: "Fehler beim Zuweisen",
        description: error.message
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold tracking-tight">Unternehmen & Kunden verwalten</h2>
            <p className="text-muted-foreground">
              Verwalten Sie Unternehmen und weisen Sie unzugeordnete Kunden zu
            </p>
          </div>
          
          <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Neues Unternehmen
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle>Neues Unternehmen erstellen</DialogTitle>
                <DialogDescription>
                  Erstellen Sie einen neuen Unternehmens-Account mit Admin-Benutzer
                </DialogDescription>
              </DialogHeader>
              
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="name">Unternehmensname *</Label>
                  <Input
                    id="name"
                    value={newTenant.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    placeholder="z.B. Café Müller"
                  />
                  {errors.name && (
                    <p className="text-sm text-destructive">{errors.name}</p>
                  )}
                </div>
                
                <div className="grid gap-2">
                  <Label htmlFor="slug">URL-Slug *</Label>
                  <Input
                    id="slug"
                    value={newTenant.slug}
                    onChange={(e) => setNewTenant(prev => ({ ...prev, slug: e.target.value }))}
                    placeholder="cafe-mueller"
                  />
                  {errors.slug && (
                    <p className="text-sm text-destructive">{errors.slug}</p>
                  )}
                </div>
                
                <div className="grid gap-2">
                  <Label htmlFor="adminEmail">Admin E-Mail *</Label>
                  <Input
                    id="adminEmail"
                    type="email"
                    value={newTenant.adminEmail}
                    onChange={(e) => setNewTenant(prev => ({ ...prev, adminEmail: e.target.value }))}
                    placeholder="admin@cafe-mueller.de"
                  />
                  {errors.adminEmail && (
                    <p className="text-sm text-destructive">{errors.adminEmail}</p>
                  )}
                </div>
                
                <div className="grid gap-2">
                  <Label htmlFor="domain">Domain (optional)</Label>
                  <Input
                    id="domain"
                    value={newTenant.domain}
                    onChange={(e) => setNewTenant(prev => ({ ...prev, domain: e.target.value }))}
                    placeholder="cafe-mueller.de"
                  />
                  {errors.domain && (
                    <p className="text-sm text-destructive">{errors.domain}</p>
                  )}
                </div>
                
                <div className="grid gap-2">
                  <Label htmlFor="status">Status</Label>
                  <Select 
                    value={newTenant.status} 
                    onValueChange={(value: 'active' | 'suspended' | 'pending') => 
                      setNewTenant(prev => ({ ...prev, status: value }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Ausstehend</SelectItem>
                      <SelectItem value="active">Aktiv</SelectItem>
                      <SelectItem value="suspended">Gesperrt</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsCreateModalOpen(false)}>
                  Abbrechen
                </Button>
                <Button onClick={createTenant} disabled={loading}>
                  {loading ? 'Erstelle...' : 'Erstellen'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Edit Tenant Dialog */}
          <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle>Unternehmen bearbeiten</DialogTitle>
                <DialogDescription>
                  Bearbeiten Sie die Unternehmensdaten
                </DialogDescription>
              </DialogHeader>
              
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="edit-name">Unternehmensname</Label>
                  <Input
                    id="edit-name"
                    value={editTenant.name}
                    onChange={(e) => setEditTenant(prev => ({ ...prev, name: e.target.value }))}
                  />
                </div>
                
                <div className="grid gap-2">
                  <Label htmlFor="edit-slug">URL-Slug</Label>
                  <Input
                    id="edit-slug"
                    value={editTenant.slug}
                    onChange={(e) => setEditTenant(prev => ({ ...prev, slug: e.target.value }))}
                  />
                </div>
                
                <div className="grid gap-2">
                  <Label htmlFor="edit-domain">Domain/Homepage</Label>
                  <Input
                    id="edit-domain"
                    value={editTenant.domain}
                    onChange={(e) => setEditTenant(prev => ({ ...prev, domain: e.target.value }))}
                    placeholder="https://example.com oder example.com"
                  />
                </div>
                
                <div className="grid gap-2">
                  <Label htmlFor="edit-status">Status</Label>
                  <Select 
                    value={editTenant.status} 
                    onValueChange={(value: 'active' | 'suspended' | 'pending') => 
                      setEditTenant(prev => ({ ...prev, status: value }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Ausstehend</SelectItem>
                      <SelectItem value="active">Aktiv</SelectItem>
                      <SelectItem value="suspended">Gesperrt</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsEditModalOpen(false)}>
                  Abbrechen
                </Button>
                <Button onClick={updateTenant} disabled={loading}>
                  {loading ? 'Speichere...' : 'Speichern'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Details Dialog */}
          <Dialog open={isDetailsModalOpen} onOpenChange={setIsDetailsModalOpen}>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle>Unternehmens-Details</DialogTitle>
                <DialogDescription>
                  Detaillierte Informationen über {selectedTenant?.name}
                </DialogDescription>
              </DialogHeader>
              
              {selectedTenant && (
                <div className="grid gap-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-muted-foreground">Name</Label>
                      <p className="text-sm">{selectedTenant.name}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-muted-foreground">URL-Slug</Label>
                      <p className="text-sm">{selectedTenant.slug}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-muted-foreground">Admin E-Mail</Label>
                      <p className="text-sm">{selectedTenant.admin_email}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-muted-foreground">Status</Label>
                      <div className="mt-1">
                        {getStatusBadge(selectedTenant.status)}
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Domain/Homepage</Label>
                    <p className="text-sm">{selectedTenant.domain || 'Keine Domain hinterlegt'}</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-muted-foreground">Erstellt am</Label>
                      <p className="text-sm">{new Date(selectedTenant.created_at).toLocaleString('de-DE')}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-muted-foreground">Tenant ID</Label>
                      <p className="text-sm font-mono text-xs">{selectedTenant.id}</p>
                    </div>
                  </div>
                </div>
              )}
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsDetailsModalOpen(false)}>
                  Schließen
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Customer Assignment Dialog */}
        <Dialog open={isAssignModalOpen} onOpenChange={setIsAssignModalOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Kunde einem Unternehmen zuweisen</DialogTitle>
              <DialogDescription>
                Wählen Sie ein Unternehmen aus, dem {selectedCustomer?.full_name} zugewiesen werden soll
              </DialogDescription>
            </DialogHeader>
            
            {selectedCustomer && (
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label className="text-sm font-medium text-muted-foreground">Kunde</Label>
                  <div className="p-3 bg-muted rounded-md">
                    <p className="font-medium">{selectedCustomer.full_name}</p>
                    <p className="text-sm text-muted-foreground">{selectedCustomer.email}</p>
                    <p className="text-xs text-muted-foreground">
                      {selectedCustomer.visit_stamps} Stempel • {selectedCustomer.total_visits} Besuche
                    </p>
                  </div>
                </div>
                
                <div className="grid gap-2">
                  <Label htmlFor="tenant-select">Unternehmen auswählen *</Label>
                  <Select 
                    value={selectedTenantForAssignment} 
                    onValueChange={setSelectedTenantForAssignment}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Unternehmen auswählen..." />
                    </SelectTrigger>
                    <SelectContent>
                      {tenants.filter(t => t.status === 'active').map((tenant) => (
                        <SelectItem key={tenant.id} value={tenant.id}>
                          {tenant.name} ({tenant.admin_email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsAssignModalOpen(false)}>
                Abbrechen
              </Button>
              <Button 
                onClick={assignCustomerToTenant} 
                disabled={loading || !selectedTenantForAssignment}
              >
                {loading ? 'Weise zu...' : 'Zuweisen'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Tabs */}
        <Tabs defaultValue="companies" className="space-y-4">
          <TabsList>
            <TabsTrigger value="companies" className="flex items-center gap-2">
              <Building className="h-4 w-4" />
              Unternehmen ({filteredTenants.length})
            </TabsTrigger>
            <TabsTrigger value="customers" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Unzugeordnete Kunden
              {unassignedCustomers.length > 0 && (
                <Badge variant="secondary" className="ml-1">
                  {unassignedCustomers.length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="companies">
            <Card>
          <CardHeader>
            <CardTitle>Unternehmen ({filteredTenants.length})</CardTitle>
            <CardDescription>
              Übersicht aller registrierten Unternehmen
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center space-x-2 mb-4">
              <Search className="h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Suchen nach Name, Slug oder Admin-E-Mail..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1"
              />
            </div>

            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Unternehmen</TableHead>
                    <TableHead>Admin</TableHead>
                    <TableHead>Domain</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Erstellt</TableHead>
                    <TableHead className="text-right">Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8">
                        Lade Unternehmen...
                      </TableCell>
                    </TableRow>
                  ) : filteredTenants.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        {searchTerm ? 'Keine Unternehmen gefunden.' : 'Noch keine Unternehmen erstellt.'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredTenants.map((tenant) => (
                      <TableRow key={tenant.id}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{tenant.name}</div>
                            <div className="text-sm text-muted-foreground">{tenant.slug}</div>
                          </div>
                        </TableCell>
                        <TableCell>{tenant.admin_email}</TableCell>
                        <TableCell>{tenant.domain || '-'}</TableCell>
                        <TableCell>{getStatusBadge(tenant.status)}</TableCell>
                        <TableCell>
                          {new Date(tenant.created_at).toLocaleDateString('de-DE')}
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" className="h-8 w-8 p-0">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleViewDetails(tenant)}>
                                <Eye className="mr-2 h-4 w-4" />
                                Details anzeigen
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleEditTenant(tenant)}>
                                <Edit className="mr-2 h-4 w-4" />
                                Bearbeiten
                              </DropdownMenuItem>
                              <DropdownMenuItem className="text-destructive">
                                <Trash2 className="mr-2 h-4 w-4" />
                                Löschen
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="customers">
        <Card>
          <CardHeader>
            <CardTitle>Unzugeordnete Kunden ({filteredCustomers.length})</CardTitle>
            <CardDescription>
              Kunden ohne zugeordnetes Unternehmen - können manuell zugewiesen werden
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center space-x-2 mb-4">
              <Search className="h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Suchen nach Name oder E-Mail..."
                value={customerSearchTerm}
                onChange={(e) => setCustomerSearchTerm(e.target.value)}
                className="flex-1"
              />
            </div>

            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Kunde</TableHead>
                    <TableHead>E-Mail</TableHead>
                    <TableHead>Aktivität</TableHead>
                    <TableHead>Registriert</TableHead>
                    <TableHead className="text-right">Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {customerLoading ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8">
                        Lade unzugeordnete Kunden...
                      </TableCell>
                    </TableRow>
                  ) : filteredCustomers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                        {customerSearchTerm ? 'Keine Kunden gefunden.' : 'Alle Kunden sind einem Unternehmen zugeordnet.'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredCustomers.map((customer) => (
                      <TableRow key={customer.id}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{customer.full_name}</div>
                            {customer.phone && (
                              <div className="text-sm text-muted-foreground">{customer.phone}</div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{customer.email}</TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <div>{customer.visit_stamps} Stempel</div>
                            <div className="text-muted-foreground">{customer.total_visits} Besuche</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {new Date(customer.created_at).toLocaleDateString('de-DE')}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            onClick={() => handleAssignCustomer(customer)}
                            disabled={tenants.filter(t => t.status === 'active').length === 0}
                          >
                            Zuweisen
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {tenants.filter(t => t.status === 'active').length === 0 && (
              <div className="mt-4 p-4 bg-muted rounded-md">
                <p className="text-sm text-muted-foreground">
                  Es sind keine aktiven Unternehmen verfügbar, denen Kunden zugewiesen werden können.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  </div>
</DashboardLayout>
  )
}

export default TenantManagement